import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getQuotes, deleteQuote, reorderQuote } from '../../services/quoteService';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Chip, TextField, MenuItem, Box, makeStyles, CircularProgress, Snackbar, Tooltip, Typography
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import AddIcon from '@material-ui/icons/Add';
import QuoteFormModal from './QuoteFormModal';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2) },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap' },
  filterField: {
    minWidth: 180,
    '& .MuiInputBase-root': {
      
    } },
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}` },
  statusPublished: {
    backgroundColor: 'rgba(16,185,129,0.15)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 600 },
  statusDraft: {
    backgroundColor: 'rgba(245,158,11,0.15)',
    color: '#f59e0b',
    border: '1px solid rgba(245,158,11,0.3)',
    fontWeight: 600 },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}` },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500 },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary } },
  deleteBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444' } },
  orderBadge: {
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '0.8rem' },
  authorText: {
    color: '#38bdf8',
    fontWeight: 600 },
  categoryBadge: {
    background: 'rgba(212,168,83,0.1)',
    color: '#d4a853',
    border: '1px solid rgba(212,168,83,0.2)',
    fontWeight: 600 },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  }
}));

export default function QuoteListPage() {
  const classes = useStyles();
  
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);

  const handleOpenCreateModal = () => {
    setSelectedQuoteId(null);
    setFormModalOpen(true);
  };

  const handleOpenEditModal = (quoteId) => {
    setSelectedQuoteId(quoteId);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedQuoteId(null);
  };

  const handleFormSuccess = () => {
    setSnackbar({
      open: true,
      message: selectedQuoteId ? 'উক্তি সফলভাবে আপডেট করা হয়েছে!' : 'নতুন উক্তি সফলভাবে তৈরি করা হয়েছে!'
    });
    loadData({ page });
  };

  const loadData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetPage = opts.page !== undefined ? opts.page : 1;
    try {
      const result = await getQuotes({ page: targetPage, status: filter });
      setItems(result.items);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      setPage(result.page);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load quotes' });
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadData({ page: 1 }); }, [loadData]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteQuote(deleteTarget.id);
      setSnackbar({ open: true, message: 'Quote removed successfully' });
      setDeleteTarget(null);
      loadData({ page });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete quote' });
    }
  };

  const handleReorder = async (item, direction) => {
    const idx = items.indexOf(item);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const target = items[targetIdx];
    try {
      await Promise.all([
        reorderQuote(item.id, target.orderIndex),
        reorderQuote(target.id, item.orderIndex),
      ]);
      loadData({ page });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reorder' });
    }
  };

  return (
    <AdminLayout title="Quotes Repository">
      <div className={classes.toolbar}>
        <div className={classes.filterRow}>
          <TextField
            select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={classes.filterField}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <Box component="span" pr={1} color="#64748b" fontSize="0.85rem" fontWeight={600}>STATUS:</Box>
            }}
          >
            <MenuItem value="all">All Records</MenuItem>
            <MenuItem value="published">Published Only</MenuItem>
            <MenuItem value="draft">Drafts Only</MenuItem>
          </TextField>
        </div>
        <Box display="flex" alignItems="center" style={{ gap: 16 }} flexWrap="wrap">
          <Chip
            label={`Total Quotes: ${totalCount}`}
            className={classes.countChip}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
            disableElevation
          >
            Add New Quote
          </Button>
        </Box>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} thickness={4} />
          <Typography className={classes.loadingText}>Loading records...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="80">Order</TableCell>
                <TableCell width="30%">Quote (English)</TableCell>
                <TableCell width="30%">Quote (Bangla)</TableCell>
                <TableCell width="120">Author</TableCell>
                <TableCell width="100">Category</TableCell>
                <TableCell width="100">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <span className={classes.orderBadge}>{item.orderIndex}</span>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textPrimary" style={{ fontWeight: 500, fontStyle: 'italic' }}>
                      "{item.quoteText?.en?.substring(0, 50)}{item.quoteText?.en?.length > 50 ? '...' : ''}"
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                      "{item.quoteText?.bn?.substring(0, 50)}{item.quoteText?.bn?.length > 50 ? '...' : ''}"
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" className={classes.authorText}>
                      {item.author || 'Anonymous'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.category && <Chip size="small" label={item.category} className={classes.categoryBadge} style={{textTransform: 'capitalize'}} />}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.isPublished ? 'Published' : 'Draft'}
                      className={item.isPublished ? classes.statusPublished : classes.statusDraft}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" style={{ gap: 6 }}>
                      <Tooltip title="Move Up">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'up')}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'down')}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton className={classes.actionBtn} onClick={() => handleOpenEditModal(item.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton className={classes.deleteBtn} onClick={() => setDeleteTarget(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: "48px 0" }} color="textSecondary">
                    No records found for the selected criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && totalCount > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={20}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Record"
        message="Are you sure you want to permanently remove this Quote from the database? This action cannot be undone."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <QuoteFormModal
        open={formModalOpen}
        quoteId={selectedQuoteId}
        onClose={handleCloseFormModal}
        onSuccess={handleFormSuccess}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}